import AddIcon from '@material-ui/icons/Add';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { createCheckin } from '../../api/goals';
import { CreateCheckin } from '../../api/types/Checkin';
import { Goal, GoalStatus } from '../../api/types/Goal';
import { SnackMessage } from '../common';
import TableActions from '../common/table-actions';
import { GoalModal } from '../founders/dashboard/GoalModal';

interface GoalsRowActionsProps {
  goal: Goal;
  variant?: 'table' | 'list';
  onGoalUpdate?: (updatedGoal: Goal) => Promise<void> | void;
}

function GoalsRowActions({
  goal,
  variant,
  onGoalUpdate,
}: GoalsRowActionsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);

  const handleCreateCheckin = useCallback(
    async (checkinData: CreateCheckin, successMessage?: string) => {
      try {
        const createdCheckin = await createCheckin(checkinData);
        const updatedGoal: Goal = {
          ...goal,
          progress: createdCheckin.progress,
          status: createdCheckin.status,
        };
        if (onGoalUpdate) {
          await onGoalUpdate(updatedGoal);
        }
        enqueueSnackbar(
          successMessage || 'The check-in was successfully added.',
          {
            variant: 'success',
          },
        );
        setCheckinModalOpen(false);
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        enqueueSnackbar(
          'An error occurred while creating the check-in. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      }
    },
    [goal, onGoalUpdate, enqueueSnackbar],
  );

  const handleArchive = useCallback(async () => {
    const isArchiving = goal.status !== 'ARCHIVED';
    const newStatus: GoalStatus = isArchiving ? 'ARCHIVED' : 'NOT_STARTED';
    const checkinData: CreateCheckin = {
      goalId: goal.id,
      progress: goal.progress,
      status: newStatus,
      contents: '',
    };
    await handleCreateCheckin(
      checkinData,
      isArchiving
        ? 'The goal was successfully archived.'
        : 'The goal was successfully unarchived.',
    );
  }, [goal, handleCreateCheckin]);

  const isArchived = goal.status === 'ARCHIVED';

  return (
    <>
      <TableActions
        variant={variant}
        actions={[
          {
            key: 'check-in',
            label: 'Check-in',
            icon: <AddIcon fontSize='small' color='inherit' />,
            onClick: () => setCheckinModalOpen(true),
          },
          {
            key: 'archive',
            label: isArchived ? 'Unarchive' : 'Archive',
            onClick: handleArchive,
          },
        ]}
      />
      {checkinModalOpen && (
        <GoalModal
          goal={goal}
          onChange={handleCreateCheckin}
          onClose={() => setCheckinModalOpen(false)}
        />
      )}
    </>
  );
}

export default GoalsRowActions;
